import React from 'react';
import { motion } from 'framer-motion';
import { FiPackage, FiTrendingUp, FiCheckCircle, FiArrowRight, FiShoppingBag, FiTruck, FiClock, FiZap, FiUsers, FiBarChart } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const QuickCommercePage = () => {
  useDocumentTitle('Quick Commerce Registration & Management');

  const features = [
    {
      icon: FiPackage,
      title: "Platform Registration",
      description: "Complete setup and registration on Zepto, Blinkit, Swiggy Instamart, and other quick commerce platforms"
    },
    {
      icon: FiShoppingBag,
      title: "Product Listing",
      description: "Professional product cataloging with optimized titles, descriptions, images, and pricing"
    },
    {
      icon: FiTruck,
      title: "Inventory Management",
      description: "Real-time stock tracking, automated replenishment alerts, and multi-platform sync"
    },
    {
      icon: FiClock,
      title: "Order Fulfillment",
      description: "Efficient order processing, packaging coordination, and delivery tracking management"
    },
    {
      icon: FiZap,
      title: "Performance Optimization",
      description: "Continuous monitoring and optimization of listings, pricing, and promotional strategies"
    },
    {
      icon: FiBarChart,
      title: "Analytics & Reporting",
      description: "Comprehensive dashboards tracking sales, inventory turnover, and customer insights"
    }
  ];

  const pricingPlans = [
    {
      name: "Starter",
      price: "₹15,000",
      period: "/month",
      description: "Perfect for brands entering quick commerce",
      features: [
        "1 Platform Registration",
        "Up to 50 SKU Management",
        "Basic Product Listing",
        "Order Processing Support",
        "Inventory Tracking",
        "Monthly Performance Report",
        "Email Support"
      ],
      highlighted: false
    },
    {
      name: "Growth",
      price: "₹35,000",
      period: "/month",
      description: "Ideal for scaling quick commerce operations",
      features: [
        "3 Platform Registration",
        "Up to 200 SKU Management",
        "Premium Product Optimization",
        "Advanced Order Management",
        "Multi-Platform Inventory Sync",
        "Bi-weekly Performance Reports",
        "Promotional Campaign Support",
        "Priority Support"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For large-scale quick commerce operations",
      features: [
        "Unlimited Platform Registration",
        "Unlimited SKU Management",
        "Full-Service Management",
        "Dedicated Account Manager",
        "Real-Time Analytics Dashboard",
        "Weekly Strategy Calls",
        "Custom Integration Support",
        "24/7 Premium Support"
      ],
      highlighted: false
    }
  ];

  const platforms = [
    { name: "Zepto", color: "purple" },
    { name: "Blinkit", color: "yellow" },
    { name: "Swiggy Instamart", color: "orange" },
    { name: "BigBasket Now", color: "green" },
    { name: "Dunzo Daily", color: "blue" },
    { name: "Amazon Fresh", color: "teal" }
  ];

  const whyChooseUs = [
    {
      title: "Quick Commerce Expertise",
      description: "Deep understanding of quick commerce platforms, algorithms, and best practices for maximum visibility"
    },
    {
      title: "End-to-End Management",
      description: "From registration to daily operations, we handle everything so you can focus on your business"
    },
    {
      title: "Proven Results",
      description: "Successfully launched 100+ brands on quick commerce platforms with average 300% sales growth in first 6 months"
    },
    {
      title: "Technology-Driven",
      description: "Proprietary tools for inventory management, price optimization, and multi-platform synchronization"
    }
  ];

  const faqs = [
    {
      question: "What is quick commerce?",
      answer: "Quick commerce (Q-commerce) refers to ultra-fast delivery services that promise delivery within 10-30 minutes. Platforms like Zepto, Blinkit, and Swiggy Instamart focus on everyday essentials, groceries, and personal care products delivered from nearby dark stores or warehouses."
    },
    {
      question: "Which platforms do you support?",
      answer: "We support all major quick commerce platforms in India including Zepto, Blinkit (formerly Grofers), Swiggy Instamart, BigBasket Now, Dunzo Daily, Amazon Fresh, and emerging regional platforms. We can also onboard you to new platforms as they launch."
    },
    {
      question: "How long does registration take?",
      answer: "Platform registration typically takes 7-14 business days depending on the platform and documentation. This includes account setup, catalog upload, inventory integration, and first order readiness. We handle all paperwork, compliance requirements, and platform communication."
    },
    {
      question: "Do I need my own inventory or warehouse?",
      answer: "Not necessarily. Quick commerce platforms operate on two models: 1) They purchase inventory from you and store it in their dark stores, or 2) You maintain inventory which they pick up for delivery. We help you determine the best model based on your product category, volume, and margins."
    },
    {
      question: "How do you optimize product listings?",
      answer: "We optimize through strategic keyword placement, high-quality images, competitive pricing analysis, compelling descriptions, accurate categorization, and promotional badge optimization. Quick commerce platforms have specific algorithms—we ensure your products rank high in search and category browsing."
    },
    {
      question: "What kind of products work best on quick commerce?",
      answer: "Fast-moving consumer goods (FMCG), groceries, snacks, beverages, personal care, baby products, health supplements, pet food, and household essentials perform best. Products should have good shelf life, convenient packaging, and appeal to impulse buying behavior typical in quick commerce."
    },
    {
      question: "How do you handle inventory management?",
      answer: "We use automated inventory tracking systems that monitor stock levels across all platforms in real-time. You receive alerts for replenishment, we coordinate with platform warehouses, manage stockouts, and ensure optimal inventory levels to maximize sales without overstocking."
    },
    {
      question: "What are typical profit margins?",
      answer: "Quick commerce platforms typically take 15-30% commission depending on category and volume. After factoring in logistics, packaging, and platform fees, brands usually achieve 20-40% net margins. We help optimize pricing strategy to balance competitiveness and profitability while ensuring healthy margins."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-purple-600 via-pink-600 to-red-600">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-1 gap-12 items-center max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-6xl font-bold text-white mb-6">
                Quick Commerce Registration & Management
              </h1>
              <p className="text-2xl text-white/90 mb-8 leading-relaxed">
                Launch and scale your brand on Zepto, Blinkit, Swiggy Instamart, and more with expert registration and ongoing management services.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 bg-white text-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
                >
                  <span>Get Started</span>
                  <FiArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-purple-600 transition-all duration-300"
                >
                  <span>Schedule Consultation</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is Quick Commerce */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">What is Quick Commerce?</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Quick Commerce (Q-commerce) is revolutionizing retail with ultra-fast delivery in 10-30 minutes. Platforms like Zepto, Blinkit, and Swiggy Instamart are growing 200%+ annually. Our registration and management services help brands capture this massive opportunity with expert platform setup, optimized listings, inventory management, and continuous performance optimization.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                  <div className="bg-gradient-to-br from-purple-500 to-pink-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="text-3xl text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Supported Platforms */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-5xl font-bold text-center text-gray-800 mb-12">Supported Platforms</h2>
          <div className="flex flex-wrap justify-center gap-6 max-w-4xl mx-auto">
            {platforms.map((platform, index) => (
              <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 px-8 py-4 rounded-full shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <span className="text-xl font-semibold text-gray-800">{platform.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-5xl font-bold text-center text-gray-800 mb-4">Transparent Pricing</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Choose the perfect plan for your quick commerce needs. All plans include expert management and optimization.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg p-8 transform hover:scale-105 transition-all duration-300 ${
                  plan.highlighted ? 'ring-4 ring-purple-500 shadow-2xl' : 'hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-800 mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-purple-600">{plan.price}</span>
                  {plan.period && <span className="text-gray-600">{plan.period}</span>}
                </div>
                <p className="text-gray-600 mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className={`block text-center px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    plan.highlighted
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-5xl font-bold text-center text-gray-800 mb-16">Why Choose Our Quick Commerce Services?</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl hover:shadow-xl transition-all duration-300">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-5xl font-bold text-center text-gray-800 mb-4">Frequently Asked Questions</h2>
          <p className="text-center text-gray-600 mb-12">
            Common questions about quick commerce registration and management
          </p>
          
          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="font-semibold text-lg text-gray-800 cursor-pointer flex justify-between items-center">
                  {faq.question}
                  <FiCheckCircle className="text-purple-600 w-5 h-5 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Launch on Quick Commerce?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-white/90">
            Let's get your brand live on India's fastest-growing delivery platforms
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 bg-white text-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
          >
            <span>Schedule Free Consultation</span>
            <FiArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default QuickCommercePage;
